import { useEffect, useRef, useState } from "react";
import { Eye, EyeOff, Loader2, X } from "lucide-react";
import { api } from "../../connect/api";
import { Avatar, dayLabel } from "./ui";

/** CEO-only: "Seen by N of M" under a CEO message — opens a live sheet listing who has opened it and who has not. */
export const SeenByButton = ({ postId, count, total, className = "", endpoint }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={`inline-flex items-center gap-1 text-[11px] font-medium text-amber-300/90 hover:text-amber-200 ${className}`} data-testid={`seen-by-button-${postId}`}>
        <Eye size={12} /> Seen by {count ?? 0}{total ? ` of ${total}` : ""}
      </button>
      {open && <SeenBySheet postId={postId} endpoint={endpoint} onClose={() => setOpen(false)} />}
    </>
  );
};

const Row = ({ s, at }) => (
  <li className="flex items-center gap-2.5 py-1.5" data-testid={`seen-by-row-${s.id}`}>
    <Avatar name={s.name} src={s.avatar} size={30} />
    <div className="min-w-0 flex-1">
      <div className="text-sm text-slate-100 truncate">{s.name}</div>
      <div className="text-[11px] text-slate-500 truncate">{s.title ? `${s.title} · ` : ""}{s.department}</div>
    </div>
    {at && <div className="text-[11px] text-emerald-300 shrink-0">{dayLabel(at)}</div>}
  </li>
);

export const SeenBySheet = ({ postId, onClose, endpoint }) => {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("seen");
  const timer = useRef(null);
  useEffect(() => {
    let alive = true;
    const url = endpoint || `/admin/seen-by/${postId}`;
    const load = () => api.get(url).then((r) => { if (alive) setD(r.data); }).catch(() => {});
    load();
    timer.current = setInterval(load, 8000);
    return () => { alive = false; clearInterval(timer.current); };
  }, [postId, endpoint]);
  const pct = d && d.total ? Math.round((100 * d.seen.length) / d.total) : 0;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-[2px]" onClick={onClose} data-testid="seen-by-sheet">
      <div className="w-full sm:max-w-md max-h-[85vh] flex flex-col rounded-t-2xl sm:rounded-2xl bg-[#0e1626] border border-white/10 shadow-2xl nxc-fade-in" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-white/10 flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-100 flex items-center gap-2"><Eye size={15} className="text-amber-300" /> Read receipts <span className="text-[10px] font-normal text-emerald-300 inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> live</span></div>
            {d && <div className="text-[11px] text-slate-400 mt-0.5 truncate">{d.group} · {d.text}</div>}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/10 text-slate-400" aria-label="Close" data-testid="seen-by-close"><X size={16} /></button>
        </div>
        {!d ? <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-slate-500" /></div> : (
          <>
            <div className="px-4 pt-3">
              <div className="flex items-baseline justify-between text-xs text-slate-300"><span><span className="text-xl font-semibold text-slate-100" data-testid="seen-by-count">{d.seen.length}</span> of {d.total} staff have opened this</span><span className="text-amber-300 font-medium">{pct}%</span></div>
              <div className="mt-1.5 h-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-gradient-to-r from-amber-500 to-amber-300 transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
            </div>
            <div className="flex px-4 mt-3 border-b border-white/10">
              {[["seen", `Seen (${d.seen.length})`, Eye], ["unseen", `Not yet (${d.unseen.length})`, EyeOff]].map(([k, l, I]) => (
                <button key={k} onClick={() => setTab(k)} className={`flex-1 pb-2 text-xs font-semibold inline-flex items-center justify-center gap-1.5 border-b-2 ${tab === k ? "border-amber-400 text-amber-300" : "border-transparent text-slate-500"}`} data-testid={`seen-by-tab-${k}`}><I size={12} /> {l}</button>
              ))}
            </div>
            <ul className="flex-1 overflow-y-auto px-4 py-2 divide-y divide-white/5" data-testid={`seen-by-list-${tab}`}>
              {tab === "seen" && d.seen.length === 0 && <li className="py-8 text-center text-xs text-slate-500">Nobody has opened this yet — the list updates automatically.</li>}
              {tab === "unseen" && d.unseen.length === 0 && <li className="py-8 text-center text-xs text-emerald-300">Everyone has seen this message.</li>}
              {tab === "seen" ? [...d.seen].reverse().map((s) => <Row key={s.id} s={s} at={s.at} />) : d.unseen.map((s) => <Row key={s.id} s={s} />)}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};

/** Staff side: report All Staff posts as seen once they scroll into view (batched, each post once per session). */
export const useFeedSeenTracker = (enabled) => {
  const sent = useRef(new Set());
  const pending = useRef(new Set());
  const obs = useRef(null);
  useEffect(() => {
    if (!enabled || typeof IntersectionObserver === "undefined") return;
    const flush = () => {
      const ids = [...pending.current]; pending.current.clear();
      if (ids.length) api.post("/group/seen", { post_ids: ids }).catch(() => {});
    };
    obs.current = new IntersectionObserver((entries) => {
      entries.forEach((e) => { const id = e.target.dataset.postId; if (e.isIntersecting && id && !sent.current.has(id)) { sent.current.add(id); pending.current.add(id); } });
    }, { threshold: 0.5 });
    const t = setInterval(flush, 2500);
    return () => { clearInterval(t); flush(); obs.current?.disconnect(); };
  }, [enabled]);
  return (el) => { if (el && obs.current) obs.current.observe(el); };
};
